import './PatternColorblockPills.scss';

import React from 'react';
import type {
  IPatternColorblockPill,
  IPatternColorblockSwatch,
} from '@/common/interfaces/print-decor.interface';

import Popper from '@/components/Popper/Popper';

interface PatternColorblockPillsProps {
  className?: string;
  pills: IPatternColorblockPill[];
}

/** Solid / CSS pattern swatches use background; custom artwork uses an <img> instead. */
const swatchStyle = (swatch: IPatternColorblockSwatch): React.CSSProperties | undefined => {
  if (swatch.imageUrl) {
    return undefined;
  }

  return { background: swatch.background || swatch.colorHex };
};

/**
 * Renders the "Patterns and/or Colorblock" choices made in the Design Tool: one pill per garment
 * part, or a single pill when one pattern covers the whole garment.
 */
const PatternColorblockPills: React.FC<PatternColorblockPillsProps> = ({ className, pills }) => {
  if (!pills.length) {
    return null;
  }

  return (
    <div className={`pattern-colorblock-pills ${className ?? ''}`}>
      {pills.map((pill) => (
        <div key={pill.partId} className="pattern-colorblock-pill">
          <span className="pattern-colorblock-pill__text">{pill.text}</span>
          {pill.swatches.map((swatch) => (
            <Popper
              key={`${pill.partId}-${swatch.kind}`}
              content={<span className="pattern-colorblock-tooltip">{swatch.label}</span>}
              placement="top"
              overlayClassName="pattern-colorblock-popper"
            >
              <span
                className={`pattern-colorblock-swatch pattern-colorblock-swatch--${swatch.kind}`}
                style={swatchStyle(swatch)}
                aria-label={swatch.label}
              >
                {swatch.imageUrl ? (
                  <img
                    className="pattern-colorblock-swatch__image"
                    src={swatch.imageUrl}
                    alt=""
                    aria-hidden
                  />
                ) : null}
              </span>
            </Popper>
          ))}
        </div>
      ))}
    </div>
  );
};

PatternColorblockPills.displayName = 'PatternColorblockPills';

export default PatternColorblockPills;
